import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Trophy, ArrowDown, ArrowUp, Search } from "lucide-react";
import { fmtNum, fmtPct } from "@/lib/verizonSalesApi";
import { StoreInfoCells, StoreInfoHead } from "./StoreInfoCells";
import { ExportButton, TotalsRow } from "./ReportTotals";

const overallOf = (s) => s.overall?.rank ?? null;
const valueOf = (s, k) => (k === "overall" ? overallOf(s) : s.lb?.[k]);

const STATUS_STYLE = {
  Open: "bg-emerald-50 text-emerald-700", "Soft Open": "bg-sky-50 text-sky-700",
  Suspended: "bg-amber-50 text-amber-700", Closed: "bg-red-50 text-red-700",
};

export const pctTone = (p) => (p == null ? "" : p >= 1 ? "text-emerald-600" : p >= 0.85 ? "text-amber-600" : "text-red-600");

export const StatusChip = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`}>{status || "—"}</span>
);

export const StoreLeaderboard = ({ stores, columns = [], onSelect, showStoreInfo = false, embedded = false }) => {
  const [sortKey, setSortKey] = useState("overall");
  const [asc, setAsc] = useState(true);
  const [q, setQ] = useState("");

  const ranked = useMemo(() => [...stores].sort((a, b) => (overallOf(a) ?? 1e9) - (overallOf(b) ?? 1e9))
    .map((s, i) => ({ ...s, pos: i + 1 })), [stores]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return ranked
      .filter((s) => !term || [s.store_id, s.door_tsp, s.city, s.address, s.sub_agent_name, s.location?.name]
        .some((v) => String(v || "").toLowerCase().includes(term)))
      .sort((a, b) => {
        const av = valueOf(a, sortKey); const bv = valueOf(b, sortKey);
        if (av == null || bv == null) return av == null ? (bv == null ? 0 : 1) : -1;
        return asc ? av - bv : bv - av;
      });
  }, [ranked, sortKey, asc, q]);

  const toggle = (k) => (k === sortKey ? setAsc(!asc) : (setSortKey(k), setAsc(k === "overall")));
  const arrow = (k) => sortKey === k && (asc ? <ArrowUp className="inline w-3 h-3 ml-0.5" /> : <ArrowDown className="inline w-3 h-3 ml-0.5" />);
  const th = "px-2 py-2 text-center cursor-pointer select-none hover:text-navy whitespace-nowrap border-l border-slate-100";

  // Percent columns: avg shown as % (values are fractions, so scale ×100 for the totals row / export)
  const lbCols = columns.map((c, ci) => (c.format === "percent"
    ? { label: c.label, total: "avg", pct: true, get: (s) => (s.lb?.[ci] == null ? null : Number(s.lb[ci]) * 100) }
    : { label: c.label, total: "sum", get: (s) => (s.lb?.[ci] == null ? null : Number(s.lb[ci])) }));

  return (
    <Card className="border-slate-200" data-testid="vz-leaderboard">
      <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Store Leaderboard</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">Overall Rank is company-wide: weighted average % of goal across the KPIs chosen by Prime Media.</p>
        </div>
        <div className="flex items-center gap-2">
          {!embedded && <ExportButton title="Store Leaderboard" rows={rows} testId="vz-leaderboard-export" cols={[...(showStoreInfo ? [{ label: "Dealer", get: (s) => s.sub_agent_name }, { label: "Address", get: (s) => [s.address, s.city].filter(Boolean).join(", ") }] : []), { label: "Store ID", get: (s) => s.store_id }, { label: "Overall Rank", get: (s) => overallOf(s) ?? null }, ...lbCols]} />}
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search store…" className="h-8 pl-8 w-48 text-xs" data-testid="vz-leaderboard-search" />
        </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-hidden px-0">
        <table className="vz-compact w-full" data-testid="vz-leaderboard-table">
          <thead>
            <tr className="text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2 w-8 text-left">#</th>
              {showStoreInfo && <StoreInfoHead className="px-2 py-2 text-left" />}
              <th className="px-2 py-2 text-left">Store ID</th>
              <th className={th} onClick={() => toggle("overall")} data-testid="vz-sort-overall">Overall Rank{arrow("overall")}</th>
              {columns.map((c, ci) => (
                <th key={ci} className={th} onClick={() => toggle(ci)} data-testid={`vz-sort-col-${ci}`}>{c.label}{arrow(ci)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.door_tsp} onClick={() => onSelect(s)} className="border-b border-slate-100 hover:bg-teal/5 cursor-pointer transition-colors" data-testid={`vz-row-${s.door_tsp}`}>
                <td className="px-4 py-2 font-semibold text-slate-400" data-testid={`vz-row-${s.door_tsp}-pos`}>{s.pos}</td>
                {showStoreInfo && <StoreInfoCells r={s} className="px-2 py-2" />}
                <td className="px-2 py-2 font-medium text-navy whitespace-nowrap" title={`${s.address}, ${s.city}`}>{s.store_id}</td>
                <td className="px-2 py-2 text-center font-semibold text-navy tabular-nums border-l border-slate-100" data-testid={`vz-row-${s.door_tsp}-overall`}
                  title={s.overall ? `${(s.overall.score * 100).toFixed(1)}% of goals` : "Not ranked (no Verizon quota)"}>{overallOf(s) ?? "—"}</td>
                {columns.map((c, ci) => (
                  <td key={ci} className="px-2 py-2 text-center tabular-nums border-l border-slate-100" data-testid={`vz-row-${s.door_tsp}-col-${ci}`}>
                    {c.format === "percent" ? fmtPct(s.lb?.[ci]) : fmtNum(s.lb?.[ci])}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length + 3} className="text-center text-slate-500 py-8">No stores match.</td></tr>}
          </tbody>
          <TotalsRow rows={rows} lead={(showStoreInfo ? 4 : 2) + 1} label={`Total (${rows.length})`} cols={lbCols} />
        </table>
      </CardContent>
    </Card>
  );
};
